import React, {Fragment, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import SpinnerDiv from "../Components/SpinnerDiv";
import PassportTable, {xlateBootstrapTableColumns} from "root/Tables/PassportTable";

import {
    TableActionIconEdit, TableActionIconView, TableActionIconGeneric, TableActionIconReply,
    customHeaderStyle,
    defaultTableHeaderStyles, defaultTopLeftCellStyle, defaultTopRightCellStyle, defaultPaginationOptions
} from "root/Tables/BaseTableComponents";

import {
    MessageFilters,
    MessageReadStatus,
    MessageStatus,
    QueueType,
    UserRoleGroups, UserTypes
} from "root/DataFunctions/Constants";
import {useLocation} from "react-router-dom";
import queryString from "query-string";
import phpVars from 'phpVars';
import {useRecoilValue} from "recoil";
import {currentChildAtom} from "root/recoil/childId";
import {useGlobalContext} from "root/contexts/GlobalContext";

const MessagesTable = ({child_uid, loading, data, clickHandler, showFullMessage }) => {
    const {search} = useLocation();
    const queryParams = queryString.parse(search);
    const tableTypeRef = useRef(null);

    const { userType } = useGlobalContext() ;

    useEffect(() => {
        tableTypeRef.current = queryParams.type === undefined ? MessageFilters.INCOMING : queryParams.type;
    }, [queryParams.type]);

    const onClickItem = (post_id, mode, sender_user_id=null) =>
    {
        //Sender_user_id is set if this is a reply to a message.
        const to_user_id = sender_user_id === null ? child_uid : sender_user_id;
        clickHandler(mode, to_user_id, post_id);
    }

    function linkFormatter(cell, row, rowIndex, formatExtraData)
    {
        const currentChild = useRecoilValue(currentChildAtom);
        const isDraft = +(row.status) === MessageStatus.NOT_SENT;
        const isReturned = +(row.status) === MessageStatus.RETURNED;
        const isApproved = +(row.status) === MessageStatus.APPROVED;
        const isSubmitted = +(row.status) === MessageStatus.NEW;
        const isHeld = +(row.status) === MessageStatus.PENDING;
        const canEditReturned = isReturned && tableTypeRef.current === MessageFilters.INCOMING;
        const shouldShowReply = row.can_reply === true ;
        const recipientIsChild = +(row.recipient_user_type) === UserTypes.CHILD;

        //Specify if the logged in users can change the status of messages including apporoving and holding
        const isNotReceiver = phpVars.unenc_user_id !== +(row.unenc_recipient_user_id) ? true :false;
        const canChangeStatus = isNotReceiver && currentChild !== undefined && currentChild.is_editor;

        //User is current child's editor, message is incoming for approval or is held
        const canApproveIncomming = isNotReceiver && currentChild !== undefined && currentChild.is_editor &&  (+(row.queue_id) === QueueType.QUEUE_TO_APPROVE_INCOMING || +(row.queue_id) === QueueType.QUEUE_INDIVIDUAL);

        //User is current child's editor, message is outgoing for approval and selected child is the sender
        const canApproveOutgoing = currentChild !== undefined && currentChild.is_editor &&  row.unenc_sender_user_id == currentChild.idusers && +(row.queue_id) === QueueType.QUEUE_TO_APPROVE_OUTGOING;

        return (
            <Fragment>
                {(isSubmitted || isHeld) && (canApproveIncomming || canApproveOutgoing) && <TableActionIconGeneric icon='icon-components' color='var(--s360-icon-green)' text="Approve" onClick={() => onClickItem( row.post_id,'Action')}/>}
                {(isApproved && canChangeStatus && recipientIsChild) && <TableActionIconGeneric icon='icon-components' color='var(--s360-icon-red)' text="Hold back (the receiver will not be able to see the message)" onClick={() => onClickItem(row.post_id, 'Hold Back')}/>}
                {(isDraft || canEditReturned) && <TableActionIconEdit text="Edit" onClick={() => onClickItem(row.post_id, 'edit')}/>}
                {shouldShowReply && <TableActionIconReply text="Reply" onClick={() => onClickItem(row.post_id, 'reply', row.sender_user_id)}/>}
                { <TableActionIconView text="View" onClick={() => onClickItem(row.post_id, 'view')} />}
            </Fragment>
        );
    }

    function titleFormatter(cell, row, rowIndex, formatExtraData)
    {
        const ovClass = showFullMessage ? {whiteSpace: 'normal'} : {} ;

        switch (+row.is_read)
        {
            case  MessageReadStatus.READ:
                return (
                    <Fragment>
                        <span style={{fontSize: '1.5rem'}}>{row.title}<br/></span>
                        <span>{row.text}</span>
                    </Fragment>
                );

                break;

            case  MessageReadStatus.UNREAD:
                return (
                    <Fragment>
                        <span style={{fontSize: '1.6rem'}}>{row.title}<br/></span>
                        <span style={{...ovClass, color: "var(--s360-icon-blue)"}}>{row.text}</span>
                    </Fragment>
                );

            default:
                return (
                    <Fragment>
                        <span>{row.title}<br/> {row.text}</span>
                    </Fragment>
                );

                break;
        }
    }

    function toFromFormatter(cell, row, rowIndex, formatExtraData)
    {
        switch (+row.status)
        {
            case  MessageStatus.RETURNED:
                return (
                    <Fragment>
                        { tableTypeRef.current === 'incoming' && <i className="icon icon-question-circle" style={{fontSize: '1.5rem', marginRight: '7px', color: "red"}}/>}
                        { tableTypeRef.current  !== 'incoming' && <i className="icon icon-chat" style={{fontSize: '1.5rem', marginRight: '7px'}}/>}
                        <span>From: {row.posted_by_name}<br/> To: {row.toName}</span>
                    </Fragment>
                );

                break;

            case  MessageStatus.NOT_SENT:
                return (
                    <Fragment>
                        <i className="icon icon-edit" style={{fontSize: '1.5rem', marginRight: '7px'}}/>
                        <span>From: {row.posted_by_name}<br/> To: {row.toName}</span>
                    </Fragment>
                );

                break;

            default:
                return (
                    <Fragment>
                        { +row.is_read  === MessageReadStatus.UNREAD && <i className="icon icon-chat" style={{fontSize: '1.5rem', marginRight: '7px', marginBottom: '3px', color: "red"}}/>}
                        { +row.is_read  === MessageReadStatus.READ && <i className="icon icon-chat" style={{fontSize: '1.5rem', marginRight: '7px', marginBottom: '3px'}}/>}
                        <span>From: {row.posted_by_name}<br/> To: {row.toName}</span>
                    </Fragment>
                );
        }
    }

    const statusColor = ( status, queue ) =>
    {
        switch ( +status )
        {
            case MessageStatus.RETURNED:
                const newColor = queue === QueueType.QUEUE_INDIVIDUAL  ? 'red' : 'blue';
                return { color: newColor };
            case MessageStatus.NOT_SENT:
            case MessageStatus.NEW:
                return { color: 'blue' };
            case MessageStatus.APPROVED:
                return { color: 'green' };
            case MessageStatus.PENDING:
                return { color: 'red' };
            default:
                return { color: 'blue' };
        }
    }

    const statusText = ( status, queue ) =>
    {
        switch ( +status )
        {
            case MessageStatus.RETURNED:
                const text = queue === QueueType.QUEUE_INDIVIDUAL ? 'Returned' : 'In Review';
                return text;
            case MessageStatus.NOT_SENT:
                return 'Draft';
            case MessageStatus.NEW:
                return 'Submitted';
            case MessageStatus.APPROVED:
                return 'Approved';
            case MessageStatus.PENDING:
                return 'Held Back';
        }
    }


    const statusFormatter = (cell, row, rowIndex, formatExtraData) =>
    {
        const style = statusColor( row.status, +(row.queue_id) );
        const text = statusText( row.status,  +(row.queue_id) );

        return (
            <Fragment>
                <span style={style}>{text}</span><br/>
            </Fragment>
        );
    }

    const headerStyle = customHeaderStyle.bind(null, {
        default: defaultTableHeaderStyles,
        posted_date: {borderRadius: defaultTopLeftCellStyle, width: "10vw"},
        fullName: {width: "auto"},
        title: {width: "auto"},
        post_status_title: {width: "7vw"},
        edit_submit: {borderRadius: defaultTopRightCellStyle, textAlign: 'right', width: "8vw"}
    });

    const getColumns = () =>
    {
        let columns = [];

        if(userType === UserTypes.CHILD)
        {
            columns = [
                {
                    dataField: "posted_date",
                    text: "Sent On",
                    sort: true,
                    headerStyle: headerStyle,

                },
                {
                    dataField: "fullName",
                    text: "From/To",
                    textAlign: "center",
                    formatter: toFromFormatter,
                    headerStyle: headerStyle
                },
                {
                    dataField: "title",
                    text: "Title/Body",
                    textAlign: "center",
                    formatter: titleFormatter,
                    headerStyle: headerStyle,
                    style: {overflow: 'hidden'},
                },
                {
                    dataField: "edit_submit",
                    text: "Options",
                    textAlign: "right",
                    headerStyle: headerStyle,
                    align: 'right',
                    formatter: linkFormatter
                }
            ];
        }
        else{
                columns = [
                {
                    dataField: "posted_date",
                    text: "Sent On",
                    sort: true,
                    headerStyle: headerStyle,

                },
                {
                    dataField: "fullName",
                    text: "From/To",
                    textAlign: "center",
                    formatter: toFromFormatter,
                    headerStyle: headerStyle
                },
                {
                    dataField: "title",
                    text: "Title/Body",
                    textAlign: "center",
                    formatter: titleFormatter,
                    headerStyle: headerStyle,
                    style: {overflow: 'hidden'},
                },
                {
                    dataField: "post_status_title",
                    text: "Status",
                    textAlign: "center",
                    formatter: statusFormatter,
                    headerStyle: headerStyle
                },
                {
                    dataField: "edit_submit",
                    text: "Options",
                    textAlign: "right",
                    headerStyle: headerStyle,
                    align: 'right',
                    formatter: linkFormatter
                }
            ];
        }

        return columns;
    };

    const translations = xlateBootstrapTableColumns(getColumns());
    const columns = React.useMemo(() => translations, []);

    const thisTableContent = () =>
    {
        return <PassportTable data={data} columns={columns} pagination={defaultPaginationOptions}/>
    }

    return loading === true ? <SpinnerDiv/> : thisTableContent();
};

export default MessagesTable;

MessagesTable.propTypes = {
    post_id: PropTypes.string,
    data: PropTypes.array,
    loading: PropTypes.bool
};